/**
 * S17 — the table COLUMN-editing write path (the column-list sibling of
 * `tableWrites`' cell path). Resolves the view whose `table` clause the edits
 * target — the AUTHORED view (an instance inherits its parent's table, so the
 * clause is authored on the parent) — and exposes typed column ops that funnel
 * through ONE engine `setTableColumns` mutation (reorder / add / remove / rename
 * / sort). Returns `null` when column editing isn't possible (no mutation
 * service, the active view isn't a model view, or its target carries no table
 * clause) — the header then renders read-only, like the cell path.
 *
 * The column order here MUST align with `computeTableData`'s columns: both read
 * the same `TableClause.columns`, so `data.columns[i]` and the op's `from: i`
 * address the same column.
 */
import { useMemo } from "react";
import type { EvalContext, TableClause, TableGetterSpec } from "@archlang/engine";
import { arrayMove } from "@dnd-kit/sortable";
import { useModelMutationsOptional } from "../workbench/mutations.js";
import { useWorkspaceModel } from "../workbench/WorkspaceModelContext.js";
import { useEffectiveViewName } from "./useEffectiveViewName.js";
import { findView, instanceParentOf, qualifiedViewName } from "./viewRepr.js";
import { displayLabel } from "../displayLabel.js";

/** One entry in the add-column getter picker. */
export interface GetterOption {
  readonly getter: TableGetterSpec;
  /** Humanized label — the getter's leaf, `@@` prefixed for aspects. */
  readonly label: string;
  /** The raw getter text (`@repo.url`, `@@team`) — the picker's stable key. */
  readonly text: string;
}

/** Typed column ops for the table header; each rewrites the whole column list
 *  via `setTableColumns` (kept columns reuse their original source bytes). */
export interface ColumnOps {
  /** Reorder: move the column at `from` to `to` (drag-and-drop). */
  reorder(from: number, to: number): void;
  /** Remove the column at `index`. */
  remove(index: number): void;
  /** Retitle the column at `index` (empty string drops a getter title). */
  rename(index: number, title: string): void;
  /** Append a brand-new getter column. */
  add(getter: TableGetterSpec, title?: string): void;
  /** Toggle `sort by` this column's getter (clears it when already sorted). */
  toggleSort(index: number): void;
  /** The column index currently driving `sort by`, else null. */
  readonly sortedColumnIndex: number | null;
  /** Per data-column getter (null for calc columns — not sortable / no getter). */
  readonly columnGetters: readonly (TableGetterSpec | null)[];
  /** Candidate getters for the add-column picker (distinct across shown rows). */
  readonly options: readonly GetterOption[];
}

/** The raw getter text for a spec (`@repo.url`, `@@team`, `@ver.changes(…)`). */
function getterText(g: TableGetterSpec): string {
  const base = `${g.sigil}${g.path.join(".")}`;
  return g.capabilityArgs !== undefined ? `${base}(${g.capabilityArgs})` : base;
}

/** Do two getter specs address the same slot (sigil + path + args)? */
function sameGetter(a: TableGetterSpec, b: TableGetterSpec): boolean {
  return getterText(a) === getterText(b);
}

/** Enumerate distinct field/aspect getters across the shown rows, for the picker. */
function pickerOptions(ctx: EvalContext): readonly GetterOption[] {
  const seen = new Map<string, GetterOption>();
  const add = (getter: TableGetterSpec): void => {
    const text = getterText(getter);
    if (seen.has(text)) return;
    const leaf = getter.path[getter.path.length - 1] ?? "";
    const label = (getter.sigil === "@@" ? "@@" : "") + displayLabel(leaf);
    seen.set(text, { getter, label, text });
  };
  for (const node of ctx.universe.nodes) {
    for (const path of node.fields.keys()) add({ sigil: "@", path: path.split(".") });
    for (const path of node.aspects.keys()) add({ sigil: "@@", path: path.split(".") });
  }
  return [...seen.values()].sort((a, b) => a.text.localeCompare(b.text));
}

export function useTableColumnEditing(ctx: EvalContext | null): ColumnOps | null {
  const mutations = useModelMutationsOptional();
  const { resolvedModel } = useWorkspaceModel();
  const effectiveName = useEffectiveViewName();

  return useMemo<ColumnOps | null>(() => {
    if (!mutations || !resolvedModel || !effectiveName || !ctx) return null;

    // Qualified-first (findView) — a bare-only match let an owner-scoped twin
    // hijack column editing off the view the surface actually renders (r35).
    const authored = findView(resolvedModel.views, effectiveName);
    if (!authored) return null;
    // An instance inherits its parent's `table` clause — author the edit on the
    // parent, where the clause actually lives. instanceParentOf = the
    // projection's own parent resolution (findView's qualified-first pick
    // could edit a different twin than the table the surface renders — r36).
    const target = authored.instanceOf
      ? instanceParentOf(authored, resolvedModel.views)
      : authored;
    if (!target) return null;

    const index = target.clauses.findIndex((c) => c.kind === "table");
    if (index < 0) return null;
    const table = target.clauses[index] as TableClause;

    const columnGetters: (TableGetterSpec | null)[] = table.columns.map((c) =>
      c.kind === "getter"
        ? { sigil: c.getter.sigil, path: c.getter.path, ...(c.getter.capabilityArgs !== undefined ? { capabilityArgs: c.getter.capabilityArgs } : {}) }
        : null,
    );

    const sortedColumnIndex = table.sortBy
      ? columnGetters.findIndex((g) => g !== null && sameGetter(g, { sigil: table.sortBy!.sigil, path: table.sortBy!.path, ...(table.sortBy!.capabilityArgs !== undefined ? { capabilityArgs: table.sortBy!.capabilityArgs } : {}) }))
      : -1;

    const identity = table.columns.map((_, i) => ({ from: i }));
    const commit = (columns: readonly ({ from: number; title?: string | null } | { getter: TableGetterSpec; title?: string })[], sortBy?: TableGetterSpec | null): void => {
      mutations.mutateView(qualifiedViewName(target), {
        op: "setTableColumns",
        index,
        columns,
        ...(sortBy !== undefined ? { sortBy } : {}),
      });
    };

    return {
      reorder(from, to) {
        if (from === to) return;
        commit(arrayMove(identity, from, to));
      },
      remove(i) {
        commit(identity.filter((_, idx) => idx !== i));
      },
      rename(i, title) {
        commit(identity.map((c, idx) => (idx === i ? { from: i, title: title === "" ? null : title } : c)));
      },
      add(getter, title) {
        commit([...identity, title !== undefined ? { getter, title } : { getter }]);
      },
      toggleSort(i) {
        if (i === sortedColumnIndex) {
          commit(identity, null);
          return;
        }
        const g = columnGetters[i];
        if (!g) return; // calc column — not sortable
        commit(identity, g);
      },
      sortedColumnIndex: sortedColumnIndex < 0 ? null : sortedColumnIndex,
      columnGetters,
      options: pickerOptions(ctx),
    };
  }, [mutations, resolvedModel, effectiveName, ctx]);
}
